import { useState, useEffect, useRef } from 'react';

import "./ScanCheckout.scss";
import LandingSection from '../../components/ScanCheckout/LandingSection';
import Step1 from '../../components/ScanCheckout/Step1';
import Step2 from '../../components/ScanCheckout/Step2';
import Step3 from '../../components/ScanCheckout/Step3';
import { getStripeProducts, StripeProduct } from '../../api/subscription';
import { fireCheckoutStart, signalScanCheckoutStep } from '../../utils/dataLayer';

type ScanType = 'lite' | 'full' | null;
type CheckoutStep = 'landing' | 'step1' | 'step2' | 'step3';

const ScanCheckout = () => {
    const [scanType, setScanType] = useState<ScanType>(
        () => (sessionStorage.getItem('scanType') as ScanType) || null
    );

    const [currentStep, setCurrentStep] = useState<CheckoutStep>(() => {
        const storedStep = (sessionStorage.getItem('scanCheckoutStep') as CheckoutStep) || 'landing';
        const storedScanType = (sessionStorage.getItem('scanType') as ScanType) || null;

        const hasBookingData = !!localStorage.getItem('bookingData');
        const hasSignupResponse = !!localStorage.getItem('signupResponse');

        // If user selected a plan previously but didn't complete checkout,
        // don't restore to step3/step2 without required data.
        if (storedStep === 'step3' && !hasSignupResponse) return 'step1';
        if (storedStep === 'step2' && !hasBookingData) return 'step1';

        // If scanType isn't selected, always show the landing selection.
        if (!storedScanType) return 'landing';

        return storedStep;
    });
    const [products, setProducts] = useState<StripeProduct[]>([]);

    const hasFetchedProducts = useRef(false);
    const hasPushedHistoryRef = useRef(false);
    const isPopNavigationRef = useRef(false);

    useEffect(() => {
        sessionStorage.setItem('scanCheckoutStep', currentStep);
        if (scanType) {
            sessionStorage.setItem('scanType', scanType);
        } else {
            sessionStorage.removeItem('scanType');
        }
    }, [currentStep, scanType]);

    // Fetch stripe products on mount
    useEffect(() => {
        if (hasFetchedProducts.current) return;
        hasFetchedProducts.current = true;

        const fetchProducts = async () => {
            try {
                const response = await getStripeProducts();
                const activeProducts = response.products.filter((product: StripeProduct) => product.active);
                setProducts(activeProducts);
            } catch (err: any) {
                console.error('Error fetching products:', err);
            }
        };
        fetchProducts();
    }, []);


    useEffect(() => {
        if (currentStep === 'landing') {
            localStorage.removeItem('bookingData');
            localStorage.removeItem('signupResponse');
            localStorage.removeItem('appliedPromocode');
            localStorage.removeItem('subscriptionAmount');
        }
    }, [currentStep]);


    useEffect(() => {
        fireCheckoutStart();
    }, []);


    useEffect(() => {
        if (currentStep === 'step1') {
            signalScanCheckoutStep('centre');
        } else if (currentStep === 'step2') {
            signalScanCheckoutStep('details');
        }
    }, [currentStep]);

    
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [currentStep]);

    // Browser Back/Forward should move between checkout steps (no extra UI buttons).
    // This also prevents the "refresh/back keeps showing Step 3" confusion.
    useEffect(() => {
        const stepValues: CheckoutStep[] = ['landing', 'step1', 'step2', 'step3'];
        if (!stepValues.includes(currentStep)) return;

        // Push an entry only after initial mount.
        if (hasPushedHistoryRef.current && !isPopNavigationRef.current) {
            try {
                window.history.pushState(
                    { scanCheckoutStep: currentStep },
                    '',
                    window.location.href
                );
            } catch {
                // ignore
            }
        }

        if (!hasPushedHistoryRef.current) {
            hasPushedHistoryRef.current = true;
        }
    }, [currentStep]);

    useEffect(() => {
        const stepValues: CheckoutStep[] = ['landing', 'step1', 'step2', 'step3'];

        const onPopState = (event: PopStateEvent) => {
            const state: any = event.state;
            const step = state?.scanCheckoutStep as CheckoutStep | undefined;
            if (!step || !stepValues.includes(step)) return;

            isPopNavigationRef.current = true;
            setCurrentStep(step);
            setTimeout(() => {
                isPopNavigationRef.current = false;
            }, 0);
        };

        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    
    const scanFullProduct = products.find(p => p.key === 'full_body_mri') || null;
    const scanLiteProduct = products.find(p => p.key === 'full_body_lite') || null;

    const getSelectedProduct = (): StripeProduct | null => {
        if (scanType === 'full') return scanFullProduct;
        if (scanType === 'lite') return scanLiteProduct;
        return null;
    };

    
    const handleSelectScan = (type: 'lite' | 'full') => {
        setScanType(type);
        setCurrentStep('step1');
    };

    return (
        <div className='full-container' >
            <div className='full-container-inner' >

                {currentStep === 'landing' && (
                    <LandingSection
                        onSelectScan={handleSelectScan}
                        scanFullProduct={scanFullProduct}
                        scanLiteProduct={scanLiteProduct}
                    />
                )}

                {currentStep === 'step1' && (
                    <Step1
                        onContinue={() => setCurrentStep('step2')}
                        selectedProduct={getSelectedProduct()}
                    />
                )}

                {currentStep === 'step2' && (
                    <Step2
                        onContinue={() => setCurrentStep('step3')}
                    />
                )}

                {currentStep === 'step3' && (
                    <Step3 />
                )}
            </div>
        </div>
    );
};

export default ScanCheckout;
